import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { 
  MdArrowBack, 
  MdEdit, 
  MdDelete, 
  MdAdd, 
  MdPerson,
  MdEmail,
  MdPhone,
  MdLocationOn,
  MdBusiness,
  MdCheckCircle,
  MdCancel,
  MdClose,
  MdChevronRight,
  MdUpload,
  MdImage,
  MdCrop,
  MdRotateLeft,
  MdRotateRight,
  MdZoomIn,
  MdZoomOut,
  MdSettings,
  MdRemoveCircle
} from 'react-icons/md';
import { CiMenuKebab } from 'react-icons/ci';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import api from '../config/axios';
import { useLanguage } from '../context/LanguageContext';
import { BASE_URL } from '../config/environment';

// Logo URL helper fonksiyonu
const getLogoUrl = (logoPath) => {
  if (!logoPath) return null;
  // Eğer logoPath zaten tam URL ise (http ile başlıyorsa) direkt döndür
  if (logoPath.startsWith('http')) {
    return logoPath;
  }
  // Eğer relative path ise BASE_URL ile birleştir
  return `${BASE_URL}${logoPath}`;
};

const SuperAdminTenantDetailPage = () => {
  const { t } = useLanguage();
  const { uuid } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [tenant, setTenant] = useState(null);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAgentModal, setShowAgentModal] = useState(false);
  const [showEditAgentModal, setShowEditAgentModal] = useState(false);
  const [showDeleteAgentModal, setShowDeleteAgentModal] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [popup, setPopup] = useState({
    show: false,
    title: '',
    message: '',
    type: 'success',
    onConfirm: null,
    showCancel: false
  });
  const [selectedLogo, setSelectedLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [showLogoEditor, setShowLogoEditor] = useState(false);
  const [crop, setCrop] = useState({
    unit: '%',
    width: 0,
    height: 0,
    x: 0,
    y: 0
  });
  const [imageRotation, setImageRotation] = useState(0);
  const [imageScale, setImageScale] = useState(1);
  const [imageOffset, setImageOffset] = useState({ x: 0, y: 0 }); // pan için
  const [originalImage, setOriginalImage] = useState(null); // HTMLImageElement
  const [selectedFileInfo, setSelectedFileInfo] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const offsetStartRef = useRef({ x: 0, y: 0 });
  const canvasRef = useRef(null);
  const [showPasswordTooltip, setShowPasswordTooltip] = useState(false);
  const [showEditPasswordTooltip, setShowEditPasswordTooltip] = useState(false);
  const [popupMessage, setPopupMessage] = useState('');
  const [showPopup, setShowPopup] = useState(false);
  const [showTenantActionsMenu, setShowTenantActionsMenu] = useState(false);
  const [showOfficialActionsMenu, setShowOfficialActionsMenu] = useState(null);
  const [agentFormData, setAgentFormData] = useState({
    full_name: '',
    username: '',
    password: '',
    confirmPassword: '',
    agent_gsm: ''
  });

  const [editAgentFormData, setEditAgentFormData] = useState({
    full_name: '',
    username: '',
    password: '',
    confirmPassword: '',
    agent_gsm: ''
  });

  const [editFormData, setEditFormData] = useState({
    company_name: '',
    company_address: '',
    company_mail: '',
    company_phone: '',
    company_status: 1
  });

  useEffect(() => {
    fetchTenantData();
  }, [uuid]);

  // URL parametresini kontrol et ve modal'ı aç
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('showAgentModal') === 'true') {
      setShowAgentModal(true);
      // URL'den parametreyi temizle
      navigate(`/superadmin/tenants/${uuid}`, { replace: true });
    }
  }, [location.search, navigate, uuid]);

  // Canvas çizim fonksiyonu - sizin kodunuzdaki mantık
  const draw = useCallback((ctx, w, h, forOutput = false) => {
    ctx.clearRect(0, 0, w, h);
    
    // Checkerboard arka plan (PNG transparan kontrolü için) - sadece önizleme için
    if (!forOutput) {
      const size = 20; // kare boyutu
      for (let y = 0; y < h; y += size) {
        for (let x = 0; x < w; x += size) {
          const isEven = ((x / size) + (y / size)) % 2 === 0;
          ctx.fillStyle = isEven ? '#ffffff' : '#e5e7eb';
          ctx.fillRect(x, y, size, size);
        }
      }
    }
    // Çıktı için hiçbir arka plan ekleme - transparanlığı koru

    if (!originalImage || !originalImage.complete) {
      ctx.save();
      ctx.fillStyle = '#94a3b8';
      ctx.font = '14px system-ui';
      ctx.textAlign = 'center';
      ctx.fillText('Bir görsel seçin veya sürükleyip bırakın', w / 2, h / 2);
      ctx.restore();
      // Önizleme için sadece çerçeve çiz
      if (!forOutput) {
        ctx.strokeStyle = '#e2e8f0';
        ctx.strokeRect(0, 0, w, h);
      }
      return;
    }

    const iw = originalImage.width;
    const ih = originalImage.height;
    const baseFit = Math.min(w / iw, h / ih); // contain

    ctx.save();
    ctx.translate(w / 2, h / 2);
    const ratio = forOutput ? 250 / 400 : 1; // pan oransal küçülsün
    ctx.translate(imageOffset.x * ratio, imageOffset.y * ratio);
    ctx.rotate((imageRotation * Math.PI) / 180);
    ctx.scale(baseFit * imageScale, baseFit * imageScale);
    ctx.drawImage(originalImage, -iw / 2, -ih / 2);
    ctx.restore();

    // Önizleme için sadece çerçeve çiz
    if (!forOutput) {
      ctx.strokeStyle = '#e2e8f0';
      ctx.strokeRect(0, 0, w, h);
    }
  }, [originalImage, imageScale, imageRotation, imageOffset]);

  // Canvas'ı her değişimde yeniden çiz
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !originalImage) return;
    const ctx = canvas.getContext('2d');
    draw(ctx, canvas.width, canvas.height, false);
  }, [originalImage, imageScale, imageRotation, imageOffset, draw]);

  // Şifre karmaşıklığı kontrolü
  const validatePassword = (password) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    
    return {
      isValid: password.length >= minLength && hasUpperCase && hasLowerCase && hasSpecialChar && hasNumber,
      errors: {
        length: password.length < minLength,
        uppercase: !hasUpperCase,
        lowercase: !hasLowerCase,
        specialChar: !hasSpecialChar,
        number: !hasNumber
      }
    };
  };

  // Telefon girişi kontrolü - Sadece rakam
  const handlePhoneInput = (e, setter, formData, field) => {
    const value = e.target.value;
    
    // Sadece rakamları al
    const numbers = value.replace(/\D/g, '');
    
    // Maksimum 10 rakam
    if (numbers.length > 10) {
      return;
    }
    
    // Sadece rakamları kaydet
    setter({...formData, [field]: numbers});
  };



  const fetchTenantData = async () => {
    try {
      const token = localStorage.getItem('superadmin_token');
      
      // Tenant bilgilerini getir
      const tenantResponse = await api.get(`/superadmin/tenants/${uuid}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTenant(tenantResponse.data);
      setEditFormData({
        company_name: tenantResponse.data.company_name,
        company_address: tenantResponse.data.company_address,
        company_mail: tenantResponse.data.company_mail,
        company_phone: tenantResponse.data.company_phone,
        company_status: tenantResponse.data.company_status
      });

      // Agent listesini getir
      const agentsResponse = await api.get(`/superadmin/tenants/${uuid}/agents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAgents(agentsResponse.data);
    } catch (error) {
      console.error('Veri yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  // Logo seçme fonksiyonu
  const handleLogoSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Dosya boyutu kontrolü (5MB)
      if (file.size > 5 * 1024 * 1024) {
                        setPopup({
                          show: true,
                          title: t('common.error'),
                          message: t('superAdmin.maxLogoSize'),
                          type: 'error',
                          onConfirm: () => setPopup({ ...popup, show: false }),
                          showCancel: false
                        });
        return;
      }

      // Dosya türü kontrolü
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/bmp', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
                        setPopup({
                          show: true,
                          title: t('common.error'),
                          message: t('superAdmin.invalidLogoType'),
                          type: 'error',
                          onConfirm: () => setPopup({ ...popup, show: false }),
                          showCancel: false
                        });
        return;
      }

      setSelectedLogo(file);
      setSelectedFileInfo({
        name: file.name,
        size: (file.size / 1024 / 1024).toFixed(2) + ' MB',
        type: file.type
      });
      
      // Logo önizlemesi oluştur
      const reader = new FileReader();
      reader.onload = (e) => {
        setOriginalImage(e.target.result);
        setLogoPreview(e.target.result);
        setShowLogoEditor(true);
        
        // HTMLImageElement oluştur - sizin kodunuzdaki mantık
        const img = new Image();
        img.onload = () => {
          setOriginalImage(img);
        };
        img.src = e.target.result;
        
        // Crop'u başlangıçta boş bırak
        setCrop({
          unit: '%',
          width: 0,
          height: 0,
          x: 0,
          y: 0
        });
        setImageRotation(0);
        setImageScale(1);
        setImageOffset({ x: 0, y: 0 });
      };
      reader.readAsDataURL(file);
    }
  };

  // Logo kırpma ve konumlandırma fonksiyonları
  const handleCropChange = (newCrop) => {
    setCrop(newCrop);
  };

  const handleRotateLeft = () => {
    setImageRotation((prev) => (prev - 90) % 360);
  };

  const handleRotateRight = () => {
    setImageRotation((prev) => (prev + 90) % 360);
  };

  const handleZoomIn = () => {
    setImageScale((prev) => Math.min(prev + 0.1, 3));
  };

  const handleZoomOut = () => {
    setImageScale((prev) => Math.max(prev - 0.1, 0.1));
  };

  const handleResetImage = () => {
    setCrop({
      unit: '%',
      width: 0,
      height: 0,
      x: 0,
      y: 0
    });
    setImageRotation(0);
    setImageScale(1);
    setImageOffset({ x: 0, y: 0 });
  };

  // Pan fonksiyonları
  const handleMouseDown = (e) => {
    if (!originalImage) return;
    setIsDragging(true);
    dragStartRef.current = { x: e.clientX, y: e.clientY };
    offsetStartRef.current = { ...imageOffset };
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    const dx = e.clientX - dragStartRef.current.x;
    const dy = e.clientY - dragStartRef.current.y;
    setImageOffset({ 
      x: offsetStartRef.current.x + dx, 
      y: offsetStartRef.current.y + dy 
    });
  };

  const handleMouseUp = () => setIsDragging(false);
  const handleMouseLeave = () => setIsDragging(false);

  // Sürükle-bırak işlemleri
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleLogoSelect({ target: { files: [files[0]] } });
    }
  };

  // Kısayol tuşları
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!showLogoEditor) return;
      
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          handleRotateLeft();
          break;
        case 'ArrowRight':
          e.preventDefault();
          handleRotateRight();
          break;
        case '+':
        case '=':
          e.preventDefault();
          handleZoomIn();
          break;
        case '-':
          e.preventDefault();
          handleZoomOut();
          break;
        case 'r':
        case 'R':
          e.preventDefault();
          handleResetImage();
          break;
        case 'Escape':
          e.preventDefault();
          setShowLogoEditor(false);
                               setSelectedLogo(null);
                     setLogoPreview(null);
                     setOriginalImage(null);
                     setSelectedFileInfo(null);
                     setIsDragging(false);
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showLogoEditor]);

  // 250x175 üret ve blob oluştur - sizin kodunuzdaki mantık
  const getCroppedImg = (imageSrc, crop) => {
    return new Promise((resolve) => {
      if (!originalImage) {
        resolve(null);
        return;
      }

      const out = document.createElement('canvas');
      out.width = 250;
      out.height = 175;
      const ctx = out.getContext('2d');
      draw(ctx, 250, 175, true);
      
      // Dosya formatını koru - PNG ise PNG, JPG ise JPG
      const fileExtension = selectedFileInfo?.name?.split('.').pop()?.toLowerCase();
      const mimeType = fileExtension === 'jpg' || fileExtension === 'jpeg' ? 'image/jpeg' : 'image/png';
      
      out.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          console.error('Failed to create blob from canvas');
          resolve(null);
        }
      }, mimeType, 0.92);
    });
  };

  // Logo yükleme fonksiyonu
  const handleLogoUpload = async () => {
    if (!selectedLogo) {
                      setPopup({
                        show: true,
                        title: t('common.warning'),
                        message: t('superAdmin.selectLogoFile') + '!',
                        type: 'error',
                        onConfirm: () => setPopup({ ...popup, show: false }),
                        showCancel: false
                      });
      return;
    }

    setUploadingLogo(true);
    try {
      const token = localStorage.getItem('superadmin_token');
      const formData = new FormData();
      
      // Kırpılmış logo oluştur (önizleme alanındaki görüntüyü birebir kaydet)
      const croppedLogo = await getCroppedImg(originalImage, crop);
      formData.append('logo', croppedLogo, 'logo.png');

              await api.post(`/superadmin/tenants/${uuid}/logo`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

                      setPopup({
                        show: true,
                        title: t('common.success'),
                        message: selectedFileInfo?.name === 'Mevcut Logo' ? t('superAdmin.logoUpdatedSuccessfully') : t('superAdmin.logoUploadedSuccessfully'),
                        type: 'success',
                        onConfirm: () => setPopup({ ...popup, show: false }),
                        showCancel: false
                      });

             // Form temizle
       setSelectedLogo(null);
       setLogoPreview(null);
       setShowLogoEditor(false);
       setOriginalImage(null);
       setSelectedFileInfo(null);
       setIsDragging(false);
      
      // Tenant verilerini yenile
      fetchTenantData();
      
      // Admin panelindeki logo'yu da güncelle (eğer bu tenant'ın agent'ı admin panelinde ise)
      if (window.refreshCompanyLogo) {
        window.refreshCompanyLogo();
      }
    } catch (error) {
      console.error('Logo yüklenirken hata:', error);
                      setPopup({
                        show: true,
                        title: t('common.error'),
                        message: `${t('superAdmin.logoUploadError')}: ${error.response?.data?.message || error.message}`,
                        type: 'error',
                        onConfirm: () => setPopup({ ...popup, show: false }),
                        showCancel: false
                      });
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('superadmin_token');
              await api.put(`/superadmin/tenants/${uuid}`, editFormData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setShowEditModal(false);
      fetchTenantData();
      setPopup({
        show: true,
        title: 'Başarılı',
        message: 'Müşteri başarıyla güncellendi!',
        type: 'success',
        onConfirm: () => setPopup({ ...popup, show: false }),
        showCancel: false
      });
    } catch (error) {
      console.error('Müşteri güncellenirken hata:', error);
      setPopup({
        show: true,
        title: 'Hata',
        message: 'Müşteri güncellenirken bir hata oluştu!',
        type: 'error',
        onConfirm: () => setPopup({ ...popup, show: false }),
        showCancel: false
      });
    }
  };

  const handleDeleteTenant = async () => {
    try {
      const token = localStorage.getItem('superadmin_token');
              await api.delete(`/superadmin/tenants/${uuid}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setShowDeleteModal(false);
      setPopup({
        show: true,
        title: 'Başarılı',
        message: 'Müşteri başarıyla silindi!',
        type: 'success',
        onConfirm: () => {
          setPopup({ ...popup, show: false });
          navigate('/superadmin/tenants');
        },
        showCancel: false
      });
    } catch (error) {
      console.error('Müşteri silinirken hata:', error);
      // Hata mesajını kullanıcıya göster
      const errorMessage = error.response?.data?.message || 'Müşteri silinirken bir hata oluştu';
      setPopup({
        show: true,
        title: 'Hata',
        message: errorMessage,
        type: 'error',
        onConfirm: () => setPopup({ ...popup, show: false }),
        showCancel: false
      });
      setShowDeleteModal(false);
    }
  };

  const handleAgentSubmit = async (e) => {
    e.preventDefault();
    
    // Form verilerini kontrol et
    if (!agentFormData.full_name || !agentFormData.username || !agentFormData.password || !agentFormData.confirmPassword || !agentFormData.agent_gsm) {
      setPopup({
        show: true,
        title: 'Uyarı',
        message: 'Lütfen tüm alanları doldurunuz!',
        type: 'error',
        onConfirm: () => setPopup({ ...popup, show: false }),
        showCancel: false
      });
      return;
    }

    // Telefon formatı kontrolü
    const phoneNumbers = agentFormData.agent_gsm.replace(/\D/g, '');
    if (phoneNumbers.length !== 10) {
      setPopup({
        show: true,
        title: 'Uyarı',
        message: 'Telefon numarası 10 haneli olmalıdır!',
        type: 'error',
        onConfirm: () => setPopup({ ...popup, show: false }),
        showCancel: false
      });
      return;
    }

    // Şifre karmaşıklığı kontrolü
    const passwordValidation = validatePassword(agentFormData.password);
    if (!passwordValidation.isValid) {
      setPopup({
        show: true,
        title: 'Uyarı',
        message: 'Şifre karmaşıklık gereksinimlerini karşılamıyor!',
        type: 'error',
        onConfirm: () => setPopup({ ...popup, show: false }),
        showCancel: false
      });
      return;
    }

    // Şifre eşleşme kontrolü
    if (agentFormData.password !== agentFormData.confirmPassword) {
      setPopup({
        show: true,
        title: 'Uyarı',
        message: 'Şifreler eşleşmiyor!',
        type: 'error',
        onConfirm: () => setPopup({ ...popup, show: false }),
        showCancel: false
      });
      return;
    }

    // Kullanıcı adı benzersizlik kontrolü
    const existingUser = agents.find(agent => agent.username === agentFormData.username);
    if (existingUser) {
      setPopup({
        show: true,
        title: 'Uyarı',
        message: 'Bu kullanıcı adı zaten kullanılıyor!',
        type: 'error',
        onConfirm: () => setPopup({ ...popup, show: false }),
        showCancel: false
      });
      return;
    }
    
    try {
      const token = localStorage.getItem('superadmin_token');
              await api.post(`/superadmin/tenants/${uuid}/agents`, agentFormData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setShowAgentModal(false);
      setShowPasswordTooltip(false);
      setAgentFormData({
        full_name: '',
        username: '',
        password: '',
        confirmPassword: '',
        agent_gsm: ''
      });
      fetchTenantData();
      setPopup({
        show: true,
        title: 'Başarılı',
        message: 'Şirket Yetkilisi başarıyla oluşturuldu!',
        type: 'success',
        onConfirm: () => setPopup({ ...popup, show: false }),
        showCancel: false
      });
    } catch (error) {
      console.error('Agent oluşturulurken hata:', error);
      setPopup({
        show: true,
        title: 'Hata',
        message: `Şirket Yetkilisi oluşturulurken bir hata oluştu: ${error.response?.data?.message || error.message}`,
        type: 'error',
        onConfirm: () => setPopup({ ...popup, show: false }),
        showCancel: false
      });
    }
  };

       // Şirket yetkilisi düzenleme fonksiyonu
  const handleEditAgent = (agent) => {
    setSelectedAgent(agent);
    setEditAgentFormData({
      full_name: agent.full_name,
      username: agent.username,
      password: '', // Şifre boş bırakılır, sadece değiştirilmek istenirse doldurulur
      confirmPassword: '',
      agent_gsm: agent.agent_gsm
    });
    setShowEditAgentModal(true);
  };

  const handleEditAgentSubmit = async (e) => {
    e.preventDefault();
    
    // Form verilerini kontrol et (şifre hariç)
    if (!editAgentFormData.full_name || !editAgentFormData.username || !editAgentFormData.agent_gsm) {
      setPopup({
        show: true,
        title: 'Uyarı',
        message: 'Lütfen tüm alanları doldurunuz!',
        type: 'error',
        onConfirm: () => setPopup({ ...popup, show: false }),
        showCancel: false
      });
      return;
    }

    // Telefon formatı kontrolü
    const phoneNumbers = editAgentFormData.agent_gsm.replace(/\D/g, '');
    if (phoneNumbers.length !== 10) {
      setPopup({
        show: true,
        title: 'Uyarı',
        message: 'Telefon numarası 10 haneli olmalıdır!',
        type: 'error',
        onConfirm: () => setPopup({ ...popup, show: false }),
        showCancel: false
      });
      return;
    }

    // Şifre değiştirilmek isteniyorsa kontrol et
    if (editAgentFormData.password.trim()) {
      // Şifre karmaşıklığı kontrolü
      const passwordValidation = validatePassword(editAgentFormData.password);
      if (!passwordValidation.isValid) {
        setPopup({
          show: true,
          title: 'Uyarı',
          message: 'Şifre karmaşıklık gereksinimlerini karşılamıyor!',
          type: 'error',
          onConfirm: () => setPopup({ ...popup, show: false }),
          showCancel: false
        });
        return;
      }

      // Şifre eşleşme kontrolü
      if (editAgentFormData.password !== editAgentFormData.confirmPassword) {
        setPopup({
          show: true,
          title: 'Uyarı',
          message: 'Şifreler eşleşmiyor!',
          type: 'error',
          onConfirm: () => setPopup({ ...popup, show: false }),
          showCancel: false
        });
        return;
      }
    }

    // Kullanıcı adı benzersizlik kontrolü (kendisi hariç)
    const existingUser = agents.find(agent => 
      agent.username === editAgentFormData.username && 
      agent.id !== selectedAgent.id
    );
    if (existingUser) {
      setPopup({
        show: true,
        title: 'Uyarı',
        message: 'Bu kullanıcı adı zaten kullanılıyor!',
        type: 'error',
        onConfirm: () => setPopup({ ...popup, show: false }),
        showCancel: false
      });
      return;
    }
    
    try {
      const token = localStorage.getItem('superadmin_token');
             const updateData = {
         full_name: editAgentFormData.full_name,
         username: editAgentFormData.username,
         agent_gsm: editAgentFormData.agent_gsm
       };

      // Şifre sadece değiştirilmek istenirse ekle
      if (editAgentFormData.password.trim()) {
        updateData.password = editAgentFormData.password;
      }

              await api.put(`/superadmin/tenants/${uuid}/agents/${selectedAgent.id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setShowEditAgentModal(false);
      setSelectedAgent(null);
      setShowEditPasswordTooltip(false);
      setEditAgentFormData({
        full_name: '',
        username: '',
        password: '',
        confirmPassword: '',
        agent_gsm: ''
      });
      fetchTenantData();
      setPopup({
        show: true,
        title: t('common.success'),
        message: t('superAdmin.agentUpdatedSuccessfully'),
        type: 'success',
        onConfirm: () => setPopup({ ...popup, show: false }),
        showCancel: false
      });
    } catch (error) {
      console.error('Agent güncellenirken hata:', error);
      setPopup({
        show: true,
        title: t('common.error'),
        message: t('superAdmin.agentUpdateError', { error: error.response?.data?.message || error.message }),
        type: 'error',
        onConfirm: () => setPopup({ ...popup, show: false }),
        showCancel: false
      });
    }
  };

  // Şirket yetkilisi silme fonksiyonu
  const handleDeleteAgent = (agent) => {
    setSelectedAgent(agent);
    setShowDeleteAgentModal(true);
  };

  const handleDeleteAgentConfirm = async () => {
    try {
      const token = localStorage.getItem('superadmin_token');
              await api.delete(`/superadmin/tenants/${uuid}/agents/${selectedAgent.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setShowDeleteAgentModal(false);
      setSelectedAgent(null);
      fetchTenantData();
      setPopup({
        show: true,
        title: t('common.success'),
        message: t('superAdmin.agentDeletedSuccessfully'),
        type: 'success',
        onConfirm: () => setPopup({ ...popup, show: false }),
        showCancel: false
      });
    } catch (error) {
      console.error('Agent silinirken hata:', error);
      setPopup({
        show: true,
        title: t('common.error'),
        message: t('superAdmin.agentDeleteError', { error: error.response?.data?.message || error.message }),
        type: 'error',
        onConfirm: () => setPopup({ ...popup, show: false }),
        showCancel: false
      });
      setShowDeleteAgentModal(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!tenant) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600 dark:text-gray-400">Müşteri bulunamadı</p>
      </div>
    );
  }

  return (
    <div>
      {/* Breadcrumb Navigation */}
      <nav className="flex items-center text-sm mb-6 pt-4">
        <div className="flex items-center bg-[#232b3b] px-4 py-2 rounded-md shadow-sm">
          <Link 
            to="/superadmin/tenants" 
            className="text-[#fff] hover:text-[gray] font-medium transition-colors duration-200"
          >
            Superadmin
          </Link>
          <MdChevronRight className="mx-2 text-[#fff]" size={20} />
          <Link 
            to="/superadmin/tenants" 
            className="text-[#fff] hover:text-[gray] font-medium transition-colors duration-200"
          >
            Tenants
          </Link>
          <MdChevronRight className="mx-2 text-[#fff]" size={20} />
          <span className="text-[#fff] font-medium">
            {tenant.company_name}
          </span>
        </div>
      </nav>

      {/* Main Content */}
      <div className="space-y-6">
        {/* Müşteri Bilgileri Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 rounded-t-lg flex justify-between items-center">
            <h1 className="text-lg font-medium text-gray-800 dark:text-white">{t('superAdmin.tenantInfo')}</h1>
            <div className="flex items-center gap-2">
              {/* Desktop Actions */}
              <div className="hidden lg:flex items-center gap-2">
                <button
                  onClick={() => setShowEditModal(true)}
                  className="flex items-center gap-1 px-3 py-1.5 bg-[#272c39] text-white text-sm rounded-md hover:bg-[#1e2230] transition-colors"
                >
                  <MdEdit size={16} />
                  {t('common.edit')}
                </button>
                <button
                  onClick={() => setShowDeleteModal(true)}
                  className="flex items-center gap-1 px-3 py-1.5 bg-[#272c39] text-white text-sm rounded-md hover:bg-[#1e2230] transition-colors"
                >
                  <MdDelete size={16} />
                  {t('common.delete')}
                </button>
              </div>

              {/* Mobile Menu */}
              <div className="lg:hidden relative">
                <button
                  onClick={() => setShowTenantActionsMenu(!showTenantActionsMenu)}
                  className="text-gray-600 hover:text-gray-900 dark:hover:text-gray-300"
                >
                  <CiMenuKebab size={18} />
                </button>

                {showTenantActionsMenu && (
                  <div className="absolute right-0 top-10 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg z-[9999]">
                    <div className="py-1">
                      <button
                        onClick={() => {
                          setShowEditModal(true);
                          setShowTenantActionsMenu(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      >
                        <MdEdit size={16} />
                        {t('common.edit')}
                      </button>
                      <button
                        onClick={() => {
                          setShowDeleteModal(true);
                          setShowTenantActionsMenu(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      >
                        <MdDelete size={16} />
                        {t('common.delete')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Desktop Table View */}
          <div className="hidden lg:block overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
              <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                <tr>
                  <td className="px-4 py-2 bg-gray-50 dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400">{t('superAdmin.companyName')}</td>
                  <td className="px-4 py-2 text-gray-900 dark:text-white">{tenant.company_name}</td>
                  <td className="px-4 py-2 bg-gray-50 dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400">{t('superAdmin.status')}</td>
                  <td className="px-4 py-2 text-gray-900 dark:text-white">
                    <span className={`inline-flex px-2 text-xs font-semibold leading-5 rounded-full ${
                      tenant.company_status 
                        ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300' 
                        : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-300'
                    }`}>
                      {tenant.company_status ? t('superAdmin.active') : t('superAdmin.inactive')}
                    </span>
                  </td>
                </tr>
                <tr>
                  <td className="px-4 py-2 bg-gray-50 dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400">{t('superAdmin.address')}</td>
                  <td className="px-4 py-2 text-gray-900 dark:text-white">{tenant.company_address}</td>
                  <td className="px-4 py-2 bg-gray-50 dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400">{t('superAdmin.email')}</td>
                  <td className="px-4 py-2 text-gray-900 dark:text-white">{tenant.company_mail}</td>
                </tr>
                <tr>
                  <td className="px-4 py-2 bg-gray-50 dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400">{t('superAdmin.phone')}</td>
                  <td className="px-4 py-2 text-gray-900 dark:text-white">{tenant.company_phone}</td>
                  <td className="px-4 py-2 bg-gray-50 dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400">{t('superAdmin.createdDate')}</td>
                  <td className="px-4 py-2 text-gray-900 dark:text-white">{new Date(tenant.created_at).toLocaleDateString()}</td>
                </tr>
                <tr>
                  <td className="px-4 py-2 bg-gray-50 dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400">{t('superAdmin.companyLogo')}</td>
                  <td className="px-4 py-2 text-gray-900 dark:text-white" colSpan="3">
                    <div className="flex items-center gap-4">
                      {/* Logo upload bölümü - Estetik sürükle-bırak */}
                      <div className="flex items-center gap-2">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleLogoSelect}
                          className="hidden"
                          id="logo-upload"
                        />
                        
                        {/* Sürükle-bırak alanı */}
                        <div 
                          className={`relative w-[100px] h-[70px] border-2 border-dashed rounded-lg transition-all duration-200 cursor-pointer hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/10 ${
                            isDragging 
                              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                              : 'border-gray-300 dark:border-gray-600'
                          }`}
                          onDragOver={handleDragOver}
                          onDragLeave={handleDragLeave}
                          onDrop={handleDrop}
                          onClick={() => document.getElementById('logo-upload').click()}
                          title={t('superAdmin.dragDropLogo')}
                        >
                          {/* Logo önizlemesi */}
                          {(logoPreview || tenant.company_logo) && (
                            <div className="relative w-full h-full">
                              <img 
                                src={logoPreview || tenant.company_logo} 
                                alt={t('superAdmin.logoPreview')} 
                                className="w-full h-full object-contain rounded"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                              {/* Çarpı işareti */}
                              <button
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  
                                  try {
                                    // Backend'e logo silme isteği gönder - Doğru token ile
                                    const token = localStorage.getItem('superadmin_token');
                                    await api.delete(`/superadmin/tenants/${uuid}/logo`, {
                                      headers: {
                                        'Authorization': `Bearer ${token}`
                                      }
                                    });
                                    
                                    // Frontend state'ini temizle
                                    setSelectedLogo(null);
                                    setLogoPreview(null);
                                    setOriginalImage(null);
                                    setSelectedFileInfo(null);
                                    
                                    // Tenant verilerini yeniden yükle
                                    fetchTenantData();
                                    
                                    // Admin panelindeki logo'yu da güncelle
                                    if (window.refreshCompanyLogo) {
                                      window.refreshCompanyLogo();
                                    }
                                    
                                    // Başarı mesajı göster
                                    setPopupMessage(t('superAdmin.logoDeletedSuccessfully'));
                                    setShowPopup(true);
                                    setTimeout(() => setShowPopup(false), 3000);
                                    
                                  } catch (error) {
                                    console.error('Logo silme hatası:', error);
                                    setPopupMessage(t('superAdmin.logoDeleteError'));
                                    setShowPopup(true);
                                    setTimeout(() => setShowPopup(false), 3000);
                                  }
                                }}
                                className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                                title={t('superAdmin.deleteLogo')}
                              >
                                <MdClose size={14} />
                              </button>
                            </div>
                          )}
                          
                          {/* Upload ikonu - sadece logo yoksa göster */}
                          {!logoPreview && !tenant.company_logo && (
                            <div className="flex flex-col items-center justify-center text-center h-full">
                              <MdUpload size={20} className="text-gray-400 dark:text-gray-500 mb-1" />
                              <p className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                {t('superAdmin.selectLogoFile')}
                              </p>
                            </div>
                          )}
                        </div>
                        
                        {selectedLogo && !showLogoEditor && (
                          <button
                            onClick={() => setShowLogoEditor(true)}
                            className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors"
                            title={t('superAdmin.editCurrentLogo')}
                          >
                            <MdCrop size={16} />
                            {t('common.edit')}
                          </button>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="lg:hidden space-y-3 p-4">
            <div className="space-y-3">
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('superAdmin.companyName')}</div>
                <div className="text-sm text-gray-900 dark:text-white font-medium">{tenant.company_name}</div>
              </div>
              
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('superAdmin.status')}</div>
                <div className="text-sm text-gray-900 dark:text-white">
                  <span className={`inline-flex px-2 text-xs font-semibold leading-5 rounded-full ${
                    tenant.company_status 
                      ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300' 
                      : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-300'
                  }`}>
                    {tenant.company_status ? t('superAdmin.active') : t('superAdmin.inactive')}
                  </span>
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('superAdmin.email')}</div>
                <div className="text-sm text-gray-900 dark:text-white">{tenant.company_mail}</div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('superAdmin.phone')}</div>
                <div className="text-sm text-gray-900 dark:text-white">{tenant.company_phone}</div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('superAdmin.address')}</div>
                <div className="text-sm text-gray-900 dark:text-white">{tenant.company_address}</div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('superAdmin.createdDate')}</div>
                <div className="text-sm text-gray-900 dark:text-white">{new Date(tenant.created_at).toLocaleDateString()}</div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">{t('superAdmin.companyLogo')}</div>
                <div className="flex items-center gap-4">
                  {/* Logo upload bölümü - Estetik sürükle-bırak */}
                  <div className="flex items-center gap-2">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoSelect}
                      className="hidden"
                      id="logo-upload-mobile"
                    />
                    
                    {/* Sürükle-bırak alanı */}
                    <div 
                      className={`relative w-[80px] h-[56px] border-2 border-dashed rounded-lg transition-all duration-200 cursor-pointer hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/10 ${
                        isDragging 
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                          : 'border-gray-300 dark:border-gray-600'
                      }`}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      onClick={() => document.getElementById('logo-upload-mobile').click()}
                      title={t('superAdmin.dragDropLogo')}
                    >
                      {/* Logo önizlemesi */}
                      {(logoPreview || tenant.company_logo) && (
                        <div className="relative w-full h-full">
                          <img 
                            src={logoPreview || getLogoUrl(tenant.company_logo)} 
                            alt={t('superAdmin.logoPreview')} 
                            className="w-full h-full object-contain rounded"
                            onError={(e) => {
                              console.log('Logo yükleme hatası:', e.target.src);
                              e.target.style.display = 'none';
                            }}
                          />
                          {/* Çarpı işareti */}
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              
                              try {
                                // Backend'e logo silme isteği gönder - Doğru token ile
                                const token = localStorage.getItem('superadmin_token');
                                await api.delete(`/superadmin/tenants/${uuid}/logo`, {
                                  headers: {
                                    'Authorization': `Bearer ${token}`
                                  }
                                });
                                
                                // Frontend state'ini temizle
                                setSelectedLogo(null);
                                setLogoPreview(null);
                                setOriginalImage(null);
                                setSelectedFileInfo(null);
                                
                                // Tenant verilerini yeniden yükle
                                fetchTenantData();
                                
                                // Admin panelindeki logo'yu da güncelle
                                if (window.refreshCompanyLogo) {
                                  window.refreshCompanyLogo();
                                }
                                
                                // Başarı mesajı göster
                                setPopupMessage(t('superAdmin.logoDeletedSuccessfully'));
                                setShowPopup(true);
                                setTimeout(() => setShowPopup(false), 3000);
                                
                              } catch (error) {
                                console.error('Logo silme hatası:', error);
                                setPopupMessage(t('superAdmin.logoDeleteError'));
                                setShowPopup(true);
                                setTimeout(() => setShowPopup(false), 3000);
                              }
                            }}
                            className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                            title={t('superAdmin.deleteLogo')}
                          >
                            <MdClose size={12} />
                          </button>
                        </div>
                      )}
                      
                      {/* Upload ikonu - sadece logo yoksa göster */}
                      {!logoPreview && !tenant.company_logo && (
                        <div className="flex flex-col items-center justify-center text-center h-full">
                          <MdUpload size={16} className="text-gray-400 dark:text-gray-500 mb-1" />
                          <p className="text-[10px] font-medium text-gray-700 dark:text-gray-300">
                            Logo
                          </p>
                        </div>
                      )}
                    </div>
                    
                    {selectedLogo && !showLogoEditor && (
                      <button
                        onClick={() => setShowLogoEditor(true)}
                        className="flex items-center gap-1 px-2 py-1 bg-blue-600 text-white text-xs rounded-md hover:bg-blue-700 transition-colors"
                        title={t('superAdmin.editCurrentLogo')}
                      >
                        <MdCrop size={12} />
                        {t('common.edit')}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Company Owner'ları Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 rounded-t-lg flex justify-between items-center">
            <h2 className="text-lg font-medium text-gray-800 dark:text-white">{t('superAdmin.companyOfficial')}</h2>
            <div className="flex items-center gap-2">
              {/* Desktop Add Button */}
              <button
                onClick={() => setShowAgentModal(true)}
                className="hidden lg:flex items-center gap-1 px-3 py-1.5 bg-[#272c39] text-white text-sm rounded-md hover:bg-[#1e2230] transition-colors"
              >
                <MdPerson size={16} />
                {t('superAdmin.addCompanyOfficial')}
              </button>

              {/* Mobile Add Button */}
              <div className="lg:hidden relative">
                <button
                  onClick={() => setShowAgentModal(true)}
                  className="w-8 h-8 bg-[#272c39] text-white rounded-md hover:bg-[#1e2230] transition-colors flex items-center justify-center"
                  title={t('superAdmin.addCompanyOfficial')}
                >
                  <MdAdd size={18} />
                </button>
              </div>
            </div>
          </div>
          
          {/* Desktop Table View */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full min-w-max table-auto">
              <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                <tr className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-700 shadow-sm">
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider bg-gray-50 dark:bg-gray-700 w-1/4">{t('superAdmin.user')}</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider bg-gray-50 dark:bg-gray-700 w-1/4">{t('superAdmin.contact')}</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider bg-gray-50 dark:bg-gray-700 w-1/6">{t('superAdmin.type')}</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider bg-gray-50 dark:bg-gray-700 w-1/6">{t('superAdmin.status')}</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider bg-gray-50 dark:bg-gray-700 w-1/6">{t('superAdmin.actions')}</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {agents.map((agent, index) => {
                  const isLast = index === agents.length - 1;
                  return (
                    <tr key={agent.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200">
                      <td className={`px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white ${isLast ? 'rounded-bl-lg' : ''}`}>
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-2">
                            <MdPerson size={14} className="text-gray-500" />
                            {agent.full_name}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {agent.username}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        <div>
                          <div className="text-sm text-gray-900 dark:text-white flex items-center gap-2">
                            <MdEmail size={14} className="text-gray-500" />
                            {agent.agent_email}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
                            <MdPhone size={14} className="text-gray-400" />
                            {agent.agent_gsm}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        <span className={`inline-flex items-center gap-1 px-2 text-xs font-semibold leading-5 rounded-full ${
                          agent.agent_type === 'Owner' 
                            ? 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-300' 
                            : 'bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-gray-300'
                        }`}>
                          {agent.agent_type}
                        </span>
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        <span className={`inline-flex items-center gap-1 px-2 text-xs font-semibold leading-5 rounded-full ${
                          agent.agent_status 
                            ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300' 
                            : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-300'
                        }`}>
                          <MdCheckCircle size={12} />
                          {agent.agent_status ? t('superAdmin.active') : t('superAdmin.inactive')}
                        </span>
                      </td>
                      <td className={`px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white ${isLast ? 'rounded-br-lg' : ''}`}>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEditAgent(agent)}
                            className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                            title="Düzenle"
                          >
                            <MdEdit size={16} />
                          </button>
                          <button
                            onClick={() => handleDeleteAgent(agent)}
                            className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                            title="Sil"
                          >
                            <MdDelete size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {agents.length === 0 && (
                  <tr>
                    <td colSpan="5" className="px-4 py-2 text-center text-sm text-gray-500 dark:text-gray-400 rounded-bl-lg rounded-br-lg">
                      Henüz şirket yetkilisi bulunmuyor
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="lg:hidden space-y-4 p-4">
            {agents.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-400 dark:text-gray-500 text-sm">Henüz şirket yetkilisi bulunmuyor</p>
              </div>
            ) : (
              agents.map((agent, index) => (
                <div key={agent.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-3">
                  {/* User Info & Type */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('superAdmin.user')}</div>
                      <div className="flex items-center gap-2">
                        <MdPerson size={14} className="text-gray-500" />
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-white">{agent.full_name}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">{agent.username}</div>
                        </div>
                      </div>
                    </div>
                    <div>
                      <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('superAdmin.type')}</div>
                      <div className="text-sm text-gray-900 dark:text-white">
                        <span className={`inline-flex items-center gap-1 px-2 text-xs font-semibold leading-5 rounded-full ${
                          agent.agent_type === 'Owner' 
                            ? 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-300' 
                            : 'bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-gray-300'
                        }`}>
                          {agent.agent_type}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Contact Info & Status */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('superAdmin.contact')}</div>
                      <div className="text-sm text-gray-900 dark:text-white space-y-1">
                        <div className="flex items-center gap-1">
                          <MdEmail size={12} className="text-gray-400" />
                          <span className="truncate text-xs">{agent.agent_email}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <MdPhone size={12} className="text-gray-400" />
                          <span className="text-xs">{agent.agent_gsm}</span>
                        </div>
                      </div>
                    </div>
                    <div>
                      <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('superAdmin.status')}</div>
                      <div className="text-sm text-gray-900 dark:text-white">
                        <span className={`inline-flex items-center gap-1 px-2 text-xs font-semibold leading-5 rounded-full ${
                          agent.agent_status 
                            ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300' 
                            : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-300'
                        }`}>
                          <MdCheckCircle size={12} />
                          {agent.agent_status ? t('superAdmin.active') : t('superAdmin.inactive')}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="grid grid-cols-2 gap-4">
                    <div></div>
                    <div>
                      <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('superAdmin.actions')}</div>
                      <div className="flex items-center gap-2">
                        <div className="relative inline-block">
                          <button
                            onClick={() => setShowOfficialActionsMenu(showOfficialActionsMenu === agent.id ? null : agent.id)}
                            className="text-gray-600 hover:text-gray-900 dark:hover:text-gray-300"
                          >
                            <CiMenuKebab size={18} />
                          </button>

                          {showOfficialActionsMenu === agent.id && (
                            <div className="absolute right-0 top-10 w-32 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg z-[9999]">
                              <div className="py-1">
                                <button
                                  onClick={() => {
                                    handleEditAgent(agent);
                                    setShowOfficialActionsMenu(null);
                                  }}
                                  className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                >
                                  <MdEdit size={16} />
                                  {t('common.edit')}
                                </button>
                                <button
                                  onClick={() => {
                                    handleDeleteAgent(agent);
                                    setShowOfficialActionsMenu(null);
                                  }}
                                  className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                >
                                  <MdDelete size={16} />
                                  {t('common.delete')}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Müşteri Düzenleme Modal */}
        {showEditModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9000] flex items-center justify-center">
            <div className="relative mx-auto w-[500px] shadow-lg rounded-md bg-white dark:bg-gray-800 overflow-hidden">
              {/* Header */}
              <div className="bg-[#272c39] text-white px-6 py-4 flex justify-between items-center">
                <h3 className="text-lg font-medium">
                  {t('superAdmin.editCustomer')}
                </h3>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="text-white hover:text-gray-200"
                >
                  <MdClose size={24} />
                </button>
              </div>
              
              <div className="p-8 bg-white dark:bg-gray-800">
                <form onSubmit={handleEditSubmit} className="space-y-4">
                  {/* Status - EN ÜSTE */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('superAdmin.status')}
                    </label>
                    <select
                      value={editFormData.company_status}
                      onChange={(e) => setEditFormData({...editFormData, company_status: parseInt(e.target.value)})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value={1}>{t('superAdmin.active')}</option>
                      <option value={0}>{t('superAdmin.inactive')}</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('superAdmin.companyName')}
                    </label>
                    <input
                      type="text"
                      value={editFormData.company_name}
                      onChange={(e) => setEditFormData({...editFormData, company_name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('superAdmin.email')}
                    </label>
                    <input
                      type="email"
                      value={editFormData.company_mail}
                      onChange={(e) => setEditFormData({...editFormData, company_mail: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('superAdmin.phone')}
                    </label>
                    <input
                      type="text"
                      value={editFormData.company_phone}
                      onChange={(e) => setEditFormData({...editFormData, company_phone: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  {/* Adres - EN ALTA, TEXTAREA */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('superAdmin.address')}
                    </label>
                    <textarea
                      value={editFormData.company_address}
                      onChange={(e) => setEditFormData({...editFormData, company_address: e.target.value})}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                      required
                    />
                  </div>

                  {/* Buttons */}
                  <div className="flex justify-end gap-3 mt-4">
                    <button
                      type="button"
                      onClick={() => setShowEditModal(false)}
                      className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500"
                    >
                      {t('superAdmin.cancel')}
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-[#272c39] text-white rounded-md hover:bg-[#1e2230]"
                    >
                      {t('superAdmin.update')}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Müşteri Silme Modal */}
        {showDeleteModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9999] flex items-center justify-center">
            <div className="relative mx-auto p-8 border w-[500px] shadow-lg rounded-md bg-white dark:bg-gray-800">
              <div className="mt-4 text-center">
                <h3 className="text-xl leading-6 font-medium text-gray-900 dark:text-white mb-4">
                  {t('superAdmin.deleteTenant')}
                </h3>
                <div className="mt-4 px-8 py-4">
                  <p className="text-base text-gray-600 dark:text-gray-400">
                    {t('superAdmin.confirmDeleteTenant', { companyName: tenant.company_name })}
                  </p>
                  <p className="text-sm text-red-600 dark:text-red-400 mt-2">
                    {t('superAdmin.deleteWarning')}
                  </p>
                </div>
                <div className="flex justify-center gap-4 px-8 py-4 mt-2">
                  <button
                    onClick={() => setShowDeleteModal(false)}
                    className="px-6 py-2.5 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 text-base font-medium rounded-md shadow-sm hover:bg-gray-300 dark:hover:bg-gray-500 focus:outline-none min-w-[120px]"
                  >
                    {t('superAdmin.cancel')}
                  </button>
                  <button
                    onClick={handleDeleteTenant}
                    className="px-6 py-2.5 bg-red-600 text-white text-base font-medium rounded-md shadow-sm hover:bg-red-700 focus:outline-none min-w-[120px]"
                  >
                    {t('superAdmin.confirmDelete')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Agent Ekleme Modal */}
        {showAgentModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9000] flex items-center justify-center">
            <div className="relative mx-auto w-[450px] shadow-lg rounded-md bg-white dark:bg-gray-800 overflow-hidden">
              {/* Header */}
              <div className="bg-[#272c39] text-white px-6 py-4 flex justify-between items-center">
                <h3 className="text-lg font-medium">
                  {t('superAdmin.addCompanyOfficial')}
                </h3>
                <button
                  onClick={() => {
                    setShowAgentModal(false);
                    setAgentFormData({
                      full_name: '',
                      username: '',
                      password: '',
                      confirmPassword: '',
                      agent_gsm: ''
                    });
                  }}
                  className="text-white hover:text-gray-200"
                >
                  <MdClose size={24} />
                </button>
              </div>
              
              <div className="p-8 bg-white dark:bg-gray-800">
                <form onSubmit={handleAgentSubmit} className="grid grid-cols-1 gap-4" autoComplete="off">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('superAdmin.fullName')}
                    </label>
                    <input
                      type="text"
                      value={agentFormData.full_name}
                      onChange={(e) => setAgentFormData({...agentFormData, full_name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('superAdmin.username')}
                    </label>
                    <input
                      type="email"
                      value={agentFormData.username}
                      onChange={(e) => setAgentFormData({...agentFormData, username: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      autoComplete="off"
                      required
                    />
                  </div>
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('superAdmin.password')}
                    </label>
                    <input
                      type="password"
                      value={agentFormData.password}
                      onChange={(e) => setAgentFormData({...agentFormData, password: e.target.value})}
                      onFocus={() => setShowPasswordTooltip(true)}
                      onBlur={() => setShowPasswordTooltip(false)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      autoComplete="new-password"
                      data-lpignore="true"
                      data-form-type="other"
                      required
                    />
                    {showPasswordTooltip && (
                      <div className="absolute z-10 mt-1 p-3 bg-gray-800 text-white text-xs rounded-lg shadow-lg border border-gray-600 min-w-[280px]">
                        <div className="font-medium mb-2">Şifre Gereksinimleri:</div>
                        <div className="grid grid-cols-2 gap-x-2 gap-y-1">
                          <div className={`${agentFormData.password.length >= 8 ? 'text-green-400' : 'text-red-400'}`}>
                            • 8+ karakter {agentFormData.password.length >= 8 ? '✓' : '✗'}
                          </div>
                          <div className={`${/[A-Z]/.test(agentFormData.password) ? 'text-green-400' : 'text-red-400'}`}>
                            • Büyük harf {/[A-Z]/.test(agentFormData.password) ? '✓' : '✗'}
                          </div>
                          <div className={`${/[a-z]/.test(agentFormData.password) ? 'text-green-400' : 'text-red-400'}`}>
                            • Küçük harf {/[a-z]/.test(agentFormData.password) ? '✓' : '✗'}
                          </div>
                          <div className={`${/[0-9]/.test(agentFormData.password) ? 'text-green-400' : 'text-red-400'}`}>
                            • Rakam {/[0-9]/.test(agentFormData.password) ? '✓' : '✗'}
                          </div>
                          <div className={`col-span-2 ${/[!@#$%^&*(),.?":{}|<>]/.test(agentFormData.password) ? 'text-green-400' : 'text-red-400'}`}>
                            • Özel karakter {/[!@#$%^&*(),.?":{}|<>]/.test(agentFormData.password) ? '✓' : '✗'}
                          </div>
                        </div>
                        <div className="absolute top-0 left-4 transform -translate-y-full">
                          <div className="w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-800"></div>
                        </div>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('superAdmin.confirmPassword')}
                    </label>
                    <input
                      type="password"
                      value={agentFormData.confirmPassword}
                      onChange={(e) => setAgentFormData({...agentFormData, confirmPassword: e.target.value})}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        agentFormData.confirmPassword && agentFormData.password !== agentFormData.confirmPassword
                          ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
                          : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700'
                      } text-gray-900 dark:text-white`}
                      autoComplete="new-password"
                      data-lpignore="true"
                      data-form-type="other"
                      required
                    />
                    {agentFormData.confirmPassword && agentFormData.password !== agentFormData.confirmPassword && (
                      <div className="mt-1 text-xs text-red-600">
                        {t('superAdmin.passwordsDoNotMatch')}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('superAdmin.phone')}
                    </label>
                    <input
                      type="tel"
                      value={agentFormData.agent_gsm}
                       onChange={(e) => handlePhoneInput(e, setAgentFormData, agentFormData, 'agent_gsm')}
                       placeholder="5XXXXXXXXX"
                       className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                       inputMode="numeric"
                       pattern="[0-9]*"
                       required
                     />
                  </div>

                  {/* Buttons */}
                  <div className="col-span-1 flex justify-end gap-3 mt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setShowAgentModal(false);
                        setShowPasswordTooltip(false);
                        setAgentFormData({
                          full_name: '',
                          username: '',
                          password: '',
                          confirmPassword: '',
                          agent_gsm: ''
                        });
                      }}
                      className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500"
                    >
                      {t('superAdmin.cancel')}
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-[#272c39] text-white rounded-md hover:bg-[#1e2230]"
                    >
                      {t('superAdmin.add')}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
                 )}

        {/* Şirket Yetkilisi Düzenleme Modal */}
        {showEditAgentModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9000] flex items-center justify-center">
            <div className="relative mx-auto w-[450px] shadow-lg rounded-md bg-white dark:bg-gray-800 overflow-hidden">
              {/* Header */}
              <div className="bg-[#272c39] text-white px-6 py-4 flex justify-between items-center">
                <h3 className="text-lg font-medium">
                  Şirket Yetkilisi Düzenle
                </h3>
                <button
                  onClick={() => {
                    setShowEditAgentModal(false);
                    setSelectedAgent(null);
                    setEditAgentFormData({
                      full_name: '',
                      username: '',
                      password: '',
                      agent_gsm: ''
                    });
                  }}
                  className="text-white hover:text-gray-200"
                >
                  <MdClose size={24} />
                </button>
              </div>
              
                             <div className="p-8 bg-white dark:bg-gray-800">
                                  <form onSubmit={handleEditAgentSubmit} className="grid grid-cols-1 gap-4" autoComplete="off">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Ad Soyad
                    </label>
                    <input
                      type="text"
                      value={editAgentFormData.full_name}
                      onChange={(e) => setEditAgentFormData({...editAgentFormData, full_name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                                     <div>
                     <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                       E-posta (Kullanıcı Adı)
                     </label>
                     <input
                       type="email"
                       value={editAgentFormData.username}
                       onChange={(e) => setEditAgentFormData({...editAgentFormData, username: e.target.value})}
                       className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                       autoComplete="off"
                       required
                     />
                   </div>
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Şifre (Değiştirmek istemiyorsanız boş bırakın)
                    </label>
                    <input
                      type="password"
                      value={editAgentFormData.password}
                      onChange={(e) => setEditAgentFormData({...editAgentFormData, password: e.target.value})}
                      onFocus={() => setShowEditPasswordTooltip(true)}
                      onBlur={() => setShowEditPasswordTooltip(false)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      autoComplete="new-password"
                      data-lpignore="true"
                      data-form-type="other"
                      placeholder="Yeni şifre (opsiyonel)"
                    />
                    {showEditPasswordTooltip && (
                      <div className="absolute z-10 mt-1 p-3 bg-gray-800 text-white text-xs rounded-lg shadow-lg border border-gray-600 min-w-[280px]">
                        <div className="font-medium mb-2">Şifre Gereksinimleri:</div>
                        <div className="grid grid-cols-2 gap-x-2 gap-y-1">
                          <div className={`${editAgentFormData.password.length >= 8 ? 'text-green-400' : 'text-red-400'}`}>
                            • 8+ karakter {editAgentFormData.password.length >= 8 ? '✓' : '✗'}
                          </div>
                          <div className={`${/[A-Z]/.test(editAgentFormData.password) ? 'text-green-400' : 'text-red-400'}`}>
                            • Büyük harf {/[A-Z]/.test(editAgentFormData.password) ? '✓' : '✗'}
                          </div>
                          <div className={`${/[a-z]/.test(editAgentFormData.password) ? 'text-green-400' : 'text-red-400'}`}>
                            • Küçük harf {/[a-z]/.test(editAgentFormData.password) ? '✓' : '✗'}
                          </div>
                          <div className={`${/[0-9]/.test(editAgentFormData.password) ? 'text-green-400' : 'text-red-400'}`}>
                            • Rakam {/[0-9]/.test(editAgentFormData.password) ? '✓' : '✗'}
                          </div>
                          <div className={`col-span-2 ${/[!@#$%^&*(),.?":{}|<>]/.test(editAgentFormData.password) ? 'text-green-400' : 'text-red-400'}`}>
                            • Özel karakter {/[!@#$%^&*(),.?":{}|<>]/.test(editAgentFormData.password) ? '✓' : '✗'}
                          </div>
                        </div>
                        <div className="absolute top-0 left-4 transform -translate-y-full">
                          <div className="w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-800"></div>
                        </div>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Şifre Tekrar
                    </label>
                    <input
                      type="password"
                      value={editAgentFormData.confirmPassword}
                      onChange={(e) => setEditAgentFormData({...editAgentFormData, confirmPassword: e.target.value})}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        editAgentFormData.confirmPassword && editAgentFormData.password !== editAgentFormData.confirmPassword
                          ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
                          : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700'
                      } text-gray-900 dark:text-white`}
                      autoComplete="new-password"
                      data-lpignore="true"
                      data-form-type="other"
                      placeholder="Yeni şifre tekrar (opsiyonel)"
                    />
                    {editAgentFormData.confirmPassword && editAgentFormData.password !== editAgentFormData.confirmPassword && (
                      <div className="mt-1 text-xs text-red-600">
                        Şifreler eşleşmiyor
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Telefon
                    </label>
                                         <input
                       type="tel"
                       value={editAgentFormData.agent_gsm}
                       onChange={(e) => handlePhoneInput(e, setEditAgentFormData, editAgentFormData, 'agent_gsm')}
                       placeholder="5XXXXXXXXX"
                       className="w-full px-3 py-2 border border-gray-300 dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                       inputMode="numeric"
                       pattern="[0-9]*"
                       required
                     />
                  </div>

                  {/* Buttons */}
                  <div className="col-span-1 flex justify-end gap-3 mt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setShowEditAgentModal(false);
                        setSelectedAgent(null);
                        setShowEditPasswordTooltip(false);
                        setEditAgentFormData({
                          full_name: '',
                          username: '',
                          password: '',
                          confirmPassword: '',
                          agent_gsm: ''
                        });
                      }}
                      className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500"
                    >
                      İptal
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-[#272c39] text-white rounded-md hover:bg-[#1e2230]"
                    >
                      Güncelle
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Şirket Yetkilisi Silme Modal */}
        {showDeleteAgentModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9999] flex items-center justify-center">
            <div className="relative mx-auto p-8 border w-[500px] shadow-lg rounded-md bg-white dark:bg-gray-800">
              <div className="mt-4 text-center">
                <h3 className="text-xl leading-6 font-medium text-gray-900 dark:text-white mb-4">
                  {t('superAdmin.deleteAgent')}
                </h3>
                <div className="mt-4 px-8 py-4">
                  <p className="text-base text-gray-600 dark:text-gray-400">
                    {t('superAdmin.confirmDeleteAgent', { agentName: selectedAgent?.full_name })}
                  </p>
                  <p className="text-sm text-red-600 dark:text-red-400 mt-2">
                    {t('superAdmin.deleteAgentWarning')}
                  </p>
                </div>
                <div className="flex justify-center gap-4 px-8 py-4 mt-2">
                  <button
                    onClick={() => {
                      setShowDeleteAgentModal(false);
                      setSelectedAgent(null);
                    }}
                    className="px-6 py-2.5 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 text-base font-medium rounded-md shadow-sm hover:bg-gray-300 dark:hover:bg-gray-500 focus:outline-none min-w-[120px]"
                  >
                    {t('superAdmin.cancel')}
                  </button>
                  <button
                    onClick={handleDeleteAgentConfirm}
                    className="px-6 py-2.5 bg-red-600 text-white text-base font-medium rounded-md shadow-sm hover:bg-red-700 focus:outline-none min-w-[120px]"
                  >
                    {t('superAdmin.confirmDelete')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

         {/* Logo Editör Modal */}
         {showLogoEditor && (
           <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9999] flex items-center justify-center">
             <div className="relative mx-auto w-[600px] shadow-lg rounded-md bg-white dark:bg-gray-800 overflow-hidden">
               {/* Header */}
                               <div className="bg-[#272c39] text-white px-6 py-4 flex justify-between items-center">
                  <h3 className="text-lg font-medium">
                    {selectedFileInfo?.name === 'Mevcut Logo' ? t('superAdmin.editCurrentLogo') : t('superAdmin.logoEditor')}
                  </h3>
                 <button
                   onClick={() => {
                     setShowLogoEditor(false);
                     setSelectedLogo(null);
                     setLogoPreview(null);
                     setOriginalImage(null);
                     setSelectedFileInfo(null);
                     setIsDragging(false);
                   }}
                   className="text-white hover:text-gray-200"
                 >
                   <MdClose size={24} />
                 </button>
               </div>
               
                                                               <div className="p-6 bg-white dark:bg-gray-800">
                                      {/* Mevcut logo bilgisi */}
                    {selectedFileInfo?.name === 'Mevcut Logo' && (
                      <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                        <p className="text-sm text-blue-700 dark:text-blue-300">
                          {t('superAdmin.logoEditInfo')}
                        </p>
                      </div>
                    )}
                    
                                      {/* Kontrol Butonları */}
                    <div className="flex items-center justify-center gap-3 mb-4">
                     <button
                       onClick={handleRotateLeft}
                       className="flex items-center justify-center w-10 h-10 bg-[#272c39] text-white rounded-lg hover:bg-[#1e2230] transition-all duration-200 shadow-md"
                       title={t('superAdmin.rotateLeft')}
                     >
                       <MdRotateLeft size={18} />
                     </button>
                     
                     <button
                       onClick={handleRotateRight}
                       className="flex items-center justify-center w-10 h-10 bg-[#272c39] text-white rounded-lg hover:bg-[#1e2230] transition-all duration-200 shadow-md"
                       title={t('superAdmin.rotateRight')}
                     >
                       <MdRotateRight size={18} />
                     </button>
                     
                     <button
                       onClick={handleZoomIn}
                       className="flex items-center justify-center w-10 h-10 bg-[#272c39] text-white rounded-lg hover:bg-[#1e2230] transition-all duration-200 shadow-md"
                       title={t('superAdmin.zoomIn')}
                     >
                       <MdZoomIn size={18} />
                     </button>
                     
                     <button
                       onClick={handleZoomOut}
                       className="flex items-center justify-center w-10 h-10 bg-[#272c39] text-white rounded-lg hover:bg-[#1e2230] transition-all duration-200 shadow-md"
                       title={t('superAdmin.zoomOut')}
                     >
                       <MdZoomOut size={18} />
                     </button>
                     
                     
                   </div>

                                     {/* Kırpma Alanı */}

                                       <div className="flex justify-center mb-4">
                      <div 
                        className={`w-[400px] h-[280px] border-2 border-dashed rounded-lg transition-colors flex items-center justify-center ${
                          isDragging 
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                            : 'border-gray-300 dark:border-gray-600'
                        }`}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                      >
                                            {originalImage ? (
                        <div className="relative w-full h-full flex items-center justify-center">
                          <canvas
                            ref={canvasRef}
                            width={400}
                            height={280}
                            className="rounded-lg border touch-none"
                            style={{
                              cursor: isDragging ? 'grabbing' : 'grab'
                            }}
                            onMouseDown={handleMouseDown}
                            onMouseMove={handleMouseMove}
                            onMouseUp={handleMouseUp}
                            onMouseLeave={handleMouseLeave}
                          />
                        </div>
                      ) : (
                        <div className="flex items-center justify-center h-48 text-gray-500 dark:text-gray-400">
                          <div className="text-center">
                            <MdUpload size={48} className="mx-auto mb-2" />
                            <p className="text-sm">{t('superAdmin.dragDropLogo')}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Dosya Bilgisi ve Butonlar */}
                  <div className="flex justify-between items-center">
                    {/* Dosya Boyutu Bilgisi */}
                    {selectedFileInfo && (
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        <span className="font-medium">{t('superAdmin.fileSize')}:</span> {selectedFileInfo.size}
                      </div>
                    )}
                    
                    {/* Butonlar */}
                    <div className="flex gap-3">
                      <button
                        onClick={() => {
                          setShowLogoEditor(false);
                          setSelectedLogo(null);
                          setLogoPreview(null);
                          setOriginalImage(null);
                          setSelectedFileInfo(null);
                          setIsDragging(false);
                        }}
                        className="px-6 py-2.5 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-500 transition-all duration-200 font-medium"
                      >
                        {t('superAdmin.cancel')}
                      </button>
                      <button
                        onClick={handleLogoUpload}
                        disabled={uploadingLogo}
                        className="px-6 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2 transition-all duration-200 font-medium shadow-md"
                      >
                        {uploadingLogo ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            {t('common.loading')}
                          </>
                                                 ) : (
                           <>
                             <MdImage size={18} />
                             {selectedFileInfo?.name === 'Mevcut Logo' ? t('superAdmin.updateLogo') : t('superAdmin.uploadLogo')}
                           </>
                         )}
                      </button>
                    </div>
                  </div>
                </div>
             </div>
           </div>
         )}

       </div>

      {/* Success/Error Popup */}
      {popup.show && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9999] flex items-center justify-center">
          <div className="relative mx-auto p-8 border w-[500px] shadow-lg rounded-md bg-white dark:bg-gray-800">
            <div className="mt-4 text-center">
              <h3 className="text-xl leading-6 font-medium text-gray-900 dark:text-white mb-4">
                {popup.title}
              </h3>
              <div className="mt-4 px-8 py-4">
                <p className="text-base text-gray-600 dark:text-gray-400">
                  {popup.message}
                </p>
              </div>
              <div className="flex justify-center gap-4 px-8 py-4 mt-2">
                {popup.showCancel && (
                  <button
                    onClick={() => setPopup({ ...popup, show: false })}
                    className="px-6 py-2.5 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 text-base font-medium rounded-md shadow-sm hover:bg-gray-300 dark:hover:bg-gray-500 focus:outline-none min-w-[120px]"
                  >
                    İptal
                  </button>
                )}
                <button
                  onClick={popup.onConfirm}
                  className={`px-6 py-2.5 text-white text-base font-medium rounded-md shadow-sm focus:outline-none min-w-[120px] ${
                    popup.type === 'error' 
                      ? 'bg-red-600 hover:bg-red-700'
                      : popup.type === 'success'
                      ? 'bg-green-600 hover:bg-green-700'
                      : 'bg-[#272c39] hover:bg-[#1e2230]'
                  }`}
                >
                  Tamam
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Logo Delete Success/Error Popup */}
      {showPopup && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9999] flex items-center justify-center">
          <div className="relative mx-auto p-6 border w-96 shadow-lg rounded-md bg-white dark:bg-gray-800">
            <div className="text-center">
              <div className="mt-2 px-4 py-3">
                <p className="text-base text-gray-600 dark:text-gray-400">
                  {popupMessage}
                </p>
              </div>
              <div className="flex justify-center px-4 py-3">
                <button
                  onClick={() => setShowPopup(false)}
                  className="px-6 py-2.5 bg-[#272c39] text-white text-base font-medium rounded-md shadow-sm hover:bg-[#1e2230] focus:outline-none min-w-[120px]"
                >
                  Tamam
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminTenantDetailPage;
